<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { MenuItem } from '@/types/layout'

import AppMenuItem from './AppMenuItem.vue'

const { t } = useI18n()
const { isAuthenticated, hasInternalAccess, hasAnyRole, userWorkspaces } = useUser()
const { unreadCount } = useNotifications()

function shouldShowMenuItem(item: MenuItem): boolean {
  if (!item.access) {
    return true
  }

  switch (item.access) {
    case 'Public':
      return true
    case 'Authenticated':
      return isAuthenticated.value
    case 'Admin':
      return hasAnyRole(['admin'])
    case 'Internal':
      return hasInternalAccess.value
    default:
      return false
  }
}

const model = computed<MenuItem[]>(() => [
  { label: t('router.pages.home'), icon: 'i-lucide-house', to: '/', access: 'Public' },
  {
    label: t('router.pages.user-notifications'),
    icon: 'i-lucide-inbox',
    to: '/user/notifications',
    access: 'Authenticated',
    badge: unreadCount.value,
  },
  {
    label: t('router.pages.user-settings'),
    icon: 'i-lucide-settings',
    to: '/user/settings',
    access: 'Authenticated',
  },
  {
    separator: true,
    access: 'Internal',
  },
  {
    label: t('router.pages.dashboard'),
    icon: 'i-lucide-chart-line',
    to: '/dashboard',
    access: 'Internal',
  },
  {
    label: t('router.pages.manage'),
    to: '/manage',
    icon: 'i-lucide-pencil',
    access: 'Internal',
  },
  {
    label: t('router.pages.workspaces'),
    icon: 'i-lucide-building-2',
    to: '/workspaces',
    access: 'Internal',
    noToggle: true,
    items: (userWorkspaces.value ?? []).map((ws) => ({
      label: ws.nameDE,
      to: `/workspaces/${ws.id}`,
    })),
  },

  {
    separator: true,
  },
  {
    label: t('router.pages.offer'),
    to: '/offer',
    icon: 'i-lucide-book',
    access: 'Public',
  },

  {
    label: t('router.pages.myregistrations'),
    to: '/registrations',
    icon: 'i-lucide-bookmark',
    access: 'Public', // keep public so that the login dialog appears if not logged in
  },
  {
    label: t('router.pages.contact'),
    to: '/contact',
    icon: 'i-lucide-mail',
    access: 'Public',
  },
  {
    separator: true,
    access: 'Admin',
  },
  {
    label: t('router.pages.admin'),
    icon: 'i-lucide-shield',
    access: 'Admin',
    items: [
      {
        label: t('router.pages.admin-overview'),
        icon: 'i-lucide-chart-bar',
        to: '/admin',
      },
      {
        label: t('router.pages.admin-activity'),
        icon: 'i-lucide-activity',
        to: '/admin/activity',
      },
      {
        label: t('router.pages.admin-users'),
        icon: 'i-lucide-users',
        to: '/admin/users',
      },
      {
        label: t('router.pages.admin-headings'),
        icon: 'i-lucide-tags',
        to: '/admin/headings',
      },
      {
        label: t('router.pages.admin-semesters'),
        icon: 'i-lucide-calendar',
        to: '/admin/semesters',
      },
      {
        label: t('router.pages.admin-language-test-sync'),
        icon: 'i-lucide-refresh-cw',
        to: '/admin/language-test-sync',
      },
      {
        label: t('router.pages.admin-system-info'),
        icon: 'i-lucide-server',
        to: '/admin/system-info',
      },
      {
        label: t('router.pages.admin-audit'),
        icon: 'i-lucide-history',
        to: '/admin/audit',
      },
      {
        label: t('router.pages.admin-trash'),
        icon: 'i-lucide-trash',
        to: '/admin/trash',
      },
      {
        label: t('router.pages.admin-toolbox'),
        icon: 'i-lucide-wrench',
        to: '/admin/toolbox',
      },
    ],
  },
])
</script>

<template>
  <ul class="layout-menu">
    <template v-for="(item, i) in model" :key="item.to ?? item.label">
      <AppMenuItem v-if="shouldShowMenuItem(item)" :item="item" :index="i" />
    </template>
  </ul>
</template>
